import { Page } from "@/types";
import { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { LayoutContext } from "../admin/layout/context/layoutcontext";
import Layout from './layout/layout';
import AppBar from './layout/AppBar'; // Import the AppBar component
import dashboardLight from "./images/byodashboardLight.png";
import dashboardDark from "./images/byodashboardDark.png";
import { 
  FaTachometerAlt, 
  FaBrain,
  FaMapMarkedAlt,
  FaMobileAlt,
  FaExclamationTriangle,
  FaShieldAlt,
  FaCity,
  FaIndustry,
  FaSeedling,
} from "react-icons/fa";
import { GiWaterDrop, GiWaterTower } from "react-icons/gi";
import { BsGraphUp } from "react-icons/bs";
import { FaFilter } from "react-icons/fa";


const LandingPage: Page = () => {
    const navigate = useNavigate();
    const { layoutConfig, colors } = useContext(LayoutContext);

    const [scrolled, setScrolled] = useState(false);

    // Handle scroll for navbar shadow
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 10);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);



    const features = [
        {
            icon: <FaTachometerAlt size={28} />,
            title: "Real-Time Monitoring",
            description: "Track water flow, pressure, and quality across your entire network with live dashboards and instant alerts.",
            color: "bg-sky-100 text-sky-600 dark:bg-sky-900/30 dark:text-sky-400",
            stats: "24/7 monitoring"
        },
        {
            icon: <FaBrain size={28} />,
            title: "AI Leak Detection",
            description: "Machine learning algorithms identify anomalies and potential leaks before they become critical issues.",
            color: "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400",
            stats: "90% accuracy"
        },
        {
            icon: <FaMapMarkedAlt size={28} />,
            title: "GIS Integration",
            description: "Interactive maps showing pipes, valves, meters, and infrastructure with real-time status updates.",
            color: "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400",
            stats: "Full visualization"
        },
        {
            icon: <BsGraphUp size={28} />,
            title: "Predictive Analytics",
            description: "Forecast demand and identify at-risk infrastructure using historical data and AI models.",
            color: "bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400",
            stats: "Predictive maintenance"
        },
        {
            icon: <FaMobileAlt size={28} />,
            title: "Mobile Workforce",
            description: "Field technicians access real-time data, submit reports, and receive alerts from any device.",
            color: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
            stats: "Anywhere access"
        },
        {
            icon: <FaFilter size={28} />,
            title: "Quality Monitoring",
            description: "Continuous tracking of pH, turbidity, chlorine, and contaminants throughout your system.",
            color: "bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400",
            stats: "Compliance ensured"
        }
    ];

    const solutions = [
        {
            title: "Municipal Water",
            icon: <FaCity size={24} />,
            features: [
                "District metered areas (DMA) management",
                "Non-revenue water reduction",
                "Citizen consumption portals",
                "Pressure zone optimization"
            ],
            color: "bg-blue-50 dark:bg-blue-900/20"
        },
        {
            title: "Industrial",
            icon: <FaIndustry size={24} />,
            features: [
                "Process water optimization",
                "Cooling tower monitoring",
                "Effluent quality compliance",
                "Water recycling tracking"
            ],
            color: "bg-purple-50 dark:bg-purple-900/20"
        },
        {
            title: "Agriculture",
            icon: <FaSeedling size={24} />,
            features: [
                "Smart irrigation control",
                "Soil moisture integration",
                "Weather-based scheduling",
                "Crop water requirement analysis"
            ],
            color: "bg-green-50 dark:bg-green-900/20"
        }
    ];

    const stats = [
        { value: "30%", label: "Water Loss Reduction", icon: <GiWaterDrop /> },
        { value: "40%", label: "Energy Savings", icon: <GiWaterTower /> },
        { value: "99.5%", label: "System Reliability", icon: <FaShieldAlt /> },
        { value: "50%", label: "Faster Leak Detection", icon: <FaExclamationTriangle /> }
    ];

    const navItems = [
      { id: 'features', label: 'Features' },
      { id: 'solutions', label: 'Solutions' },
      { id: 'contact', label: 'Contact' },
    ];

    return (
        <Layout>
            <div className="relative flex flex-column min-h-screen">
                {/* Fixed background elements */}
                <div className="fixed inset-0 overflow-hidden pointer-events-none -z-10">
                    <div className="absolute -top-40 -left-40 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl"></div>
                    <div className="absolute top-60 -right-40 w-80 h-80 bg-sky-500/10 rounded-full blur-3xl"></div>
                    <div className="absolute -bottom-40 left-1/3 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl"></div>
                </div>

                {/* Use the separated AppBar component */}
                <AppBar scrolled={scrolled} navItems={navItems}/>

                {/* Main Content - Starts Below Navbar */}
                <div className="flex-grow-1 pt-16">
                    {/* The rest of your content remains the same */}
                    <div className="py-8 px-4 mx-0 md:mx-6 lg:mx-8 lg:px-8">
                        {/* Hero Section */}
                        <div className="grid grid-nogutter justify-content-between align-items-center mb-8 py-8 md:mb-12 md:py-12">
                            <div className="col-12 md:col-6 flex flex-column gap-6">
                                <div className="flex align-items-center gap-2">
                                    <span className="font-bold px-3 py-1 border-round text-sm"
                                        //   style={{ backgroundColor: colors.primaryBg, color: colors.primaryColor }}
                                    >
                                        AI-POWERED WATER INTELLIGENCE
                                    </span>
                                    <span className="text-sm font-medium" style={{ color: colors.lightText }}>
                                        Trusted by 500+ utilities
                                    </span>
                                </div>
                                <h1 className="font-bold text-6xl line-height-2" style={{ color: colors.textColor }}>
                                    Smart Water Management for a Sustainable Future
                                </h1>
                                <p className="text-xl line-height-3" style={{ color: colors.lightText }}>
                                    Monitor, analyze, and optimize your water infrastructure in real-time with our AI-powered platform. 
                                    Reduce water loss, improve efficiency, and ensure water security for communities and industries.
                                </p>
                                <div className="flex flex-wrap gap-4 mt-3">
                                    <Button 
                                        label="Start Free Trial" 
                                        className="font-bold px-6 py-3"
                                        style={{ backgroundColor: colors.primaryColor, borderColor: colors.primaryColor }}
                                        icon="pi pi-play"
                                        size="large"
                                    />
                                    <Button 
                                        label="Watch Demo" 
                                        className="p-button-outlined font-bold px-6 py-3"
                                        style={{ borderColor: colors.primaryColor, color: colors.primaryColor }}
                                        icon="pi pi-eye"
                                        size="large"
                                    />
                                </div>
                                <div className="flex flex-wrap gap-4 mt-2">
                                    <div className="flex align-items-center gap-2">
                                        <i className="pi pi-check-circle text-green-500"></i>
                                        <span className="text-sm" style={{ color: colors.lightText }}>No credit card required</span>
                                    </div>
                                    <div className="flex align-items-center gap-2">
                                        <i className="pi pi-check-circle text-green-500"></i>
                                        <span className="text-sm" style={{ color: colors.lightText }}>30-day free trial</span>
                                    </div>
                                    <div className="flex align-items-center gap-2">
                                        <i className="pi pi-check-circle text-green-500"></i>
                                        <span className="text-sm" style={{ color: colors.lightText }}>Full support included</span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 md:col-6 mt-8 md:mt-0">
                                <div className="relative">
                                    <img
                                        src={(layoutConfig.colorScheme === "dark" || layoutConfig.colorScheme === "dim") ? dashboardDark : dashboardLight}
                                        alt="AmanziLytics Dashboard Preview"
                                        className="w-full border-round-xl shadow-4"
                                    />
                                    
                                </div>
                            </div>
                        </div>

                        {/* Stats Section */}
                        <div className="grid my-10 py-8 px-4 border-round-xl"
                             style={{ backgroundColor: colors.surfaceBg }}>
                            {stats.map((stat, index) => (
                                <div key={index} className="col-6 md:col-3 text-center p-4">
                                    <div className="flex justify-content-center mb-3">
                                        <div className="p-3 border-round" 
                                             style={{ backgroundColor: colors.primaryBg, color: colors.primaryColor }}>
                                            {stat.icon}
                                        </div>
                                    </div>
                                    <h2 className="font-bold text-4xl mb-2" style={{ color: colors.primaryColor }}>
                                        {stat.value}
                                    </h2>
                                    <p className="font-medium" style={{ color: colors.textColor }}>
                                        {stat.label}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* Features Section */}
                        <div id="features" className="my-12 py-8">
                            <div className="text-center mb-8">
                                <h2 className="font-bold text-5xl mb-4" style={{ color: colors.textColor }}>
                                    Advanced Water Management Features
                                </h2>
                                <p className="text-xl" style={{ maxWidth: '800px', margin: '0 auto', color: colors.lightText }}>
                                    Comprehensive tools for every aspect of water infrastructure management
                                </p>
                            </div>
                            <div className="grid">
                                {features.map((feature, index) => (
                                    <div key={index} className="col-12 md:col-6 lg:col-4 p-3">
                                        <div className="p-5 border-round-xl shadow-2 h-full hover:shadow-4 transition-all duration-300 cursor-pointer"
                                             style={{ 
                                                 backgroundColor: colors.cardBg,
                                                 border: `1px solid ${colors.borderColor}`
                                             }}
                                            //  onClick={() => navigate("/features")}
                                        >
                                            <div className={`flex align-items-center justify-content-center border-round-lg p-3 mb-4 ${feature.color}`}
                                                 style={{ width: '60px', height: '60px' }}>
                                                {feature.icon}
                                            </div>
                                            <h3 className="font-semibold mb-3 text-xl" style={{ color: colors.textColor }}>
                                                {feature.title}
                                            </h3>
                                            <p className="mb-4" style={{ color: colors.lightText }}>
                                                {feature.description}
                                            </p>
                                            <div className="flex align-items-center justify-content-between">
                                                <span className="text-sm font-medium" style={{ color: colors.primaryColor }}>
                                                    {feature.stats}
                                                </span>
                                                <Button 
                                                    icon="pi pi-arrow-right" 
                                                    className="p-button-text p-0"
                                                    style={{ color: colors.primaryColor }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Solutions Section */}
                        <div id="solutions" className="my-12 py-8 px-4 border-round-xl"
                             style={{ backgroundColor: colors.surfaceBg }}>
                            <div className="text-center mb-8">
                                <h2 className="font-bold text-5xl mb-4" style={{ color: colors.textColor }}>
                                    Industry-Specific Solutions
                                </h2>
                                <p className="text-xl" style={{ maxWidth: '800px', margin: '0 auto', color: colors.lightText }}>
                                    Tailored solutions for different water management challenges
                                </p>
                            </div>
                            <div className="grid">
                                {solutions.map((solution, index) => (
                                    <div key={index} className="col-12 md:col-4 p-3">
                                        <div className="p-5 border-round-xl h-full"
                                            style={{ 
                                                backgroundColor: colors.surfaceBg,
                                                border: `1px solid ${colors.borderColor}`,
                                                color: colors.textColor 
                                            }}>
                                            <div className="flex align-items-center gap-3 mb-4">
                                                <div className="p-2 border-round" style={{ 
                                                    backgroundColor: colors.primaryBg,
                                                    color: colors.primaryColor 
                                                }}>
                                                    {solution.icon}
                                                </div>
                                                <h3 className="font-semibold text-xl m-0">
                                                    {solution.title}
                                                </h3>
                                            </div>
                                            <ul className="list-none p-0 m-0">
                                                {solution.features.map((feature, idx) => (
                                                    <li key={idx} className="mb-3 flex align-items-center">
                                                        <i className="pi pi-check-circle mr-2" style={{ color: colors.primaryColor }}></i>
                                                        <span>{feature}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                            <Button 
                                                label="Learn More" 
                                                className="w-full mt-4"
                                                style={{ 
                                                    backgroundColor: colors.primaryColor, 
                                                    borderColor: colors.primaryColor,
                                                    color: 'white'
                                                }}
                                                // onClick={() => navigate(`/solutions/${solution.title.toLowerCase()}`)}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Integration Section */}
                        <div className="my-12 py-8">
                            <div className="grid align-items-center">
                                <div className="col-12 md:col-6">
                                    <h2 className="font-bold text-4xl mb-4" style={{ color: colors.textColor }}>
                                        Seamless Integration with Existing Systems
                                    </h2>
                                    <p className="text-lg mb-5" style={{ color: colors.lightText }}>
                                        Connect AmanziLytics with your current SCADA, GIS, and ERP systems for a unified view of your water operations.
                                    </p>
                                    <div className="grid">
                                        {['SCADA Systems', 'GIS Platforms', 'ERP Software', 'IoT Devices'].map((system, idx) => (
                                            <div key={idx} className="col-6">
                                                <div className="flex align-items-center mb-4">
                                                    <i className="pi pi-check-circle text-green-500 mr-2"></i>
                                                    <span style={{ color: colors.textColor }}>{system}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <Button 
                                        label="View Integration Guide" 
                                        className="mt-4"
                                        style={{ backgroundColor: colors.primaryColor, borderColor: colors.primaryColor }}
                                        icon="pi pi-book"
                                    />
                                </div>
                                <div className="col-12 md:col-6 mt-6 md:mt-0">
                                    <div className="p-6 border-round-xl shadow-2"
                                         style={{ backgroundColor: colors.cardBg }}>
                                        
                                        <h3 className="text-center font-semibold text-xl mb-4" style={{ color: colors.textColor }}>
                                            API-First Architecture
                                        </h3>
                                        <p className="text-center" style={{ color: colors.lightText }}>
                                            RESTful APIs and webhooks for easy integration with your existing tools and workflows.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* CTA Section */}
                        <div id="contact" className="text-center my-12 py-12 px-4 border-round-xl shadow-2"
                             style={{ 
                                 backgroundColor: colors.cardBg,
                                 background: `linear-gradient(135deg, ${colors.primaryBg} 0%, ${colors.surfaceBg} 100%)`
                             }}>
                            <div className="font-bold px-4 py-2 border-round inline-block mb-4"
                                 style={{ backgroundColor: colors.primaryColor, color: 'white' }}>
                                READY TO OPTIMIZE YOUR WATER MANAGEMENT?
                            </div>
                            <h2 className="font-bold text-4xl mb-3" style={{ color: colors.textColor }}>
                                Start Your Smart Water Journey Today
                            </h2>
                            <p className="text-xl mb-6 mx-auto" style={{ maxWidth: '600px', color: colors.lightText }}>
                                Join 500+ utilities and industries that trust AmanziLytics for their water management needs.
                            </p>
                            <div className="flex flex-wrap gap-4 justify-content-center">
                                <Button 
                                    label="Start Free Trial" 
                                    className="font-bold px-6 py-3"
                                    style={{ backgroundColor: colors.primaryColor, borderColor: colors.primaryColor }}
                                    icon="pi pi-play"
                                    size="large"
                                />
                                <Button 
                                    label="Schedule Demo" 
                                    className="p-button-outlined font-bold px-6 py-3"
                                    style={{ borderColor: colors.primaryColor, color: colors.primaryColor }}
                                    icon="pi pi-calendar"
                                    size="large"
                                />
                                <Button 
                                    label="Contact Sales" 
                                    className="p-button-help font-bold px-6 py-3"
                                    icon="pi pi-comments"
                                    size="large"
                                />
                            </div>
                            <p className="text-sm mt-6" style={{ color: colors.lightText }}>
                                No credit card required • 30-day free trial • Full support included
                            </p>
                        </div>
                    </div>

                    
                </div>
            </div>
        </Layout>
    );
};

export default LandingPage;